/**
 * Themed toast notification container with frog hop animations.
 * Enter: bounces in from top (hop-appear). Exit: hops upward (hop-away).
 * @module sonner
 */

import { Toaster as Sonner } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Themed toast notification container.
 * Must be rendered once in Layout.tsx.
 */
export function Toaster({ ...props }: ToasterProps) {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      position="top-center"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-card group-[.toaster]:text-foreground ' +
            'group-[.toaster]:border-border group-[.toaster]:shadow-lg ' +
            'group-[.toaster]:shadow-primary/10 ' +
            // Hop appear on enter, hop away on exit
            'data-[mounted=true]:animate-hop-appear ' +
            'data-[removed=true]:animate-hop-away',
          title: 'group-[.toast]:font-medium',
          description: 'group-[.toast]:text-muted-foreground group-[.toast]:text-sm',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground ' +
            'group-[.toast]:hover:bg-primary/90',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          error:
            'group-[.toaster]:border-destructive/50 group-[.toaster]:text-destructive ' +
            'group-[.toaster]:bg-destructive/10',
          warning:
            'group-[.toaster]:border-amber-500/50 group-[.toaster]:text-amber-700 ' +
            'group-[.toaster]:bg-amber-500/10',
          info:
            'group-[.toaster]:border-blue-500/50 group-[.toaster]:text-blue-700 ' +
            'group-[.toaster]:bg-blue-500/10'
        }
      }}
      {...props}
    />
  );
}
